import { verifyEmail } from '@/auth/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { type FormEvent, useState } from 'react'

export function VerifyEmailForm({
  className,
  email,
  pendingToken,
  onSuccess,
  ...props
}: React.ComponentProps<'div'> & {
  email: string
  pendingToken: string
  onSuccess: () => void
}) {
  const [code, setCode] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setPending(true)
    try {
      await verifyEmail(pendingToken, code)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.')
      setPending(false)
    }
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card>
        <CardContent>
          <form onSubmit={onSubmit}>
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">Verify your email</h1>
                <p className="text-balance text-muted-foreground">
                  Enter the code we sent to <span className="font-medium">{email}</span>.
                </p>
              </div>
              <Field>
                <FieldLabel htmlFor="code">Verification code</FieldLabel>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  disabled={pending}
                  autoFocus
                  required
                />
              </Field>
              {error ? (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              ) : null}
              <Field>
                <Button type="submit" disabled={pending || code.length < 4}>
                  {pending ? 'Verifying…' : 'Verify email'}
                </Button>
              </Field>
              <FieldDescription className="text-center">
                The code expires in 10 minutes.
              </FieldDescription>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
